"use client";

import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

type ErrorBody = { error?: string; issues?: { fieldErrors?: Record<string, string[]> } };

async function submit(endpoint: string, payload: unknown) {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = (await response.json()) as ErrorBody;
  if (!response.ok) throw new Error(body.error ?? "The request could not be completed.");
}

export function LoginForm() {
  const router = useRouter();
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setPending(true); setError("");
    const data = new FormData(event.currentTarget);
    try {
      await submit("/api/v1/auth/login", { email: data.get("email"), password: data.get("password") });
      router.push("/account"); router.refresh();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Sign-in failed."); }
    finally { setPending(false); }
  }
  return <form className="sidebar" onSubmit={onSubmit}>
    <label>Email address<input type="email" name="email" autoComplete="email" required /></label>
    <label>Password<input type="password" name="password" autoComplete="current-password" required /></label>
    {error && <p className="form-error" role="alert">{error}</p>}
    <button className="button" disabled={pending} type="submit">{pending ? "Signing in…" : "Sign in"}</button>
  </form>;
}

export function SignupForm() {
  const router = useRouter();
  const [role, setRole] = useState<"trainer" | "organisation">("trainer");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setPending(true); setError("");
    const data = new FormData(event.currentTarget);
    try {
      await submit("/api/v1/auth/register", {
        role, displayName: data.get("displayName"), organisationName: role === "organisation" ? data.get("organisationName") : undefined,
        email: data.get("email"), password: data.get("password"), platformCore: data.get("platformCore") === "on",
        shareWithPartners: data.get("shareWithPartners") === "on", marketing: data.get("marketing") === "on",
      });
      router.push("/account"); router.refresh();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Registration failed."); }
    finally { setPending(false); }
  }
  return <form className="sidebar" onSubmit={onSubmit}>
    <fieldset><legend>I am joining as</legend>
      <label><input type="radio" name="role" value="trainer" checked={role === "trainer"} onChange={() => setRole("trainer")} /> Trainer</label>
      <label><input type="radio" name="role" value="organisation" checked={role === "organisation"} onChange={() => setRole("organisation")} /> Organisation</label>
    </fieldset>
    <label>Your name<input type="text" name="displayName" autoComplete="name" minLength={2} maxLength={80} required /></label>
    {role === "organisation" && <label>Organisation name<input type="text" name="organisationName" autoComplete="organization" minLength={2} maxLength={160} required /></label>}
    <label>Email address<input type="email" name="email" autoComplete="email" required /></label>
    <label>Password<input type="password" name="password" autoComplete="new-password" minLength={12} maxLength={128} aria-describedby="password-help" required /></label>
    <p className="field-help" id="password-help">Use at least 12 characters with upper and lowercase letters, a number and a symbol.</p>
    <fieldset><legend>Privacy choices</legend>
      <label><input type="checkbox" name="platformCore" required /> Create my account and provide the registry service. Required.</label>
      <label><input type="checkbox" name="shareWithPartners" /> Share my details with ATCEN and other named partners for training opportunities. Optional.</label>
      <label><input type="checkbox" name="marketing" /> Send me news, events and opportunities by email. Optional.</label>
    </fieldset>
    {error && <p className="form-error" role="alert">{error}</p>}
    <button className="button" disabled={pending} type="submit">{pending ? "Creating account…" : "Create account"}</button>
  </form>;
}

export function LogoutButton() {
  const router = useRouter(); const [pending, setPending] = useState(false);
  return <button className="button" type="button" disabled={pending} onClick={async () => { setPending(true); await fetch("/api/v1/auth/logout", { method: "POST" }); router.push("/"); router.refresh(); }}>{pending ? "Signing out…" : "Sign out"}</button>;
}

type ConsentEvent = { purposeCode: string; wording: string; noticeVersion: string; granted: boolean; recordedAt: string };
export function ConsentSettings() {
  const [events, setEvents] = useState<ConsentEvent[]>([]); const [error, setError] = useState(""); const [pending, setPending] = useState("");
  async function load() { const response = await fetch("/api/v1/consents"); const body = await response.json() as { data?: ConsentEvent[]; error?: string }; if (!response.ok) throw new Error(body.error ?? "Unable to load privacy choices."); setEvents(body.data ?? []); }
  useEffect(() => { let active = true; void fetch("/api/v1/consents").then(async (response) => { const body = await response.json() as { data?: ConsentEvent[]; error?: string }; if (!response.ok) throw new Error(body.error ?? "Unable to load privacy choices."); return body.data ?? []; }).then((data) => { if (active) setEvents(data); }).catch((reason: Error) => { if (active) setError(reason.message); }); return () => { active = false; }; }, []);
  const current = new Map<string, ConsentEvent>(); for (const event of events) if (!current.has(event.purposeCode)) current.set(event.purposeCode, event);
  async function change(purposeCode: "share_with_partners" | "marketing", granted: boolean) { setPending(purposeCode); setError(""); const response = await fetch("/api/v1/consents", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ purposeCode, granted }) }); const body = await response.json() as { error?: string }; if (!response.ok) setError(body.error ?? "Unable to save this choice."); else await load(); setPending(""); }
  return <section><h2>Privacy choices</h2>{(["share_with_partners", "marketing"] as const).map((code) => { const decision = current.get(code); return <div className="consent-row" key={code}><div><strong>{code === "marketing" ? "Email news and opportunities" : "Share details with named partners"}</strong><p>{decision?.wording}</p><small>Current: {decision?.granted ? "Allowed" : "Not allowed"} · Notice {decision?.noticeVersion}</small></div><button className="button small" disabled={pending === code} type="button" onClick={() => change(code, !decision?.granted)}>{decision?.granted ? "Withdraw" : "Allow"}</button></div>; })}{error && <p className="form-error" role="alert">{error}</p>}<p><small>Every change creates a new record. Earlier records remain in your history.</small></p></section>;
}
